import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, theme, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { RootState } from 'store/configureStore';
import { managementOp } from 'store/ducks/Management';
import { useTranslation } from 'react-i18next';
import { ITaskType } from 'store/ducks/Management/types';
import { TASK_TYPE_COLUMNS, TASK_TYPE_KEYS } from 'constants/taskTypeColumns';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import CustomTable from 'components/Tables/CustomTable';
import DataManagementLayout from 'components/Layouts/DataManagementLayout/DataManagementLayout';
import useDeleteWithDependencyCheck from 'utils/hooks/useDeleteWithDependencyCheck';
import ApiService from 'utils/ApiCalls';
import * as Styled from './TaskTypeManagement.styled';

const { useToken } = theme;

const TaskTypeManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = useToken();

  const dispatch = useDispatch();
  const { drawTable, lang, saveClicked } = useSelector((state: RootState) => ({
    drawTable: state.management.drawTable,
    lang: state.global.lang,
    saveClicked: state.management.saveClicked
  }));

  const [rows, setRows] = useState<ITaskType[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialRows, setInitialRows] = useState<ITaskType[]>([]);

  useUnsavedChanges({ hasChanges, data: rows, initialData: initialRows });

  useEffect(() => {
    if (rows.length > 0 && initialRows.length === 0) {
      setInitialRows(rows);
    }
  }, [rows, initialRows]);

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const handleFieldChange = (
    rowKey: string,
    field: keyof ITaskType,
    value: string
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.key === rowKey ? { ...row, [field]: value } : row
      )
    );
    setHasChanges(true);
    dispatch(managementOp.setDataEdited(true));
  };

  const handleSave = async () => {
    try {
      const validRows = rows.filter(
        (row) => row.short_name.trim() || row.description.trim()
      );

      if (validRows.length === 0) {
        message.info('No changes to save');
        return;
      }

      // TODO: Implement API call for task types
      message.success('Task types saved successfully!');
      setHasChanges(false);
      dispatch(managementOp.setDataEdited(false));
      setInitialRows(rows);
    } catch (error) {
      console.error('Save failed:', error);
      message.error('Failed to save task types');
    }
  };

  useEffect(() => {
    if (saveClicked) {
      handleSave();
      dispatch(managementOp.setSaveClicked(false));
    }
  }, [saveClicked]);

  const handleAddRow = () => {
    const newRow: ITaskType = {
      key: `new_${Date.now()}`,
      short_name: '',
      description: ''
    };
    setRows((prevRows) => [...prevRows, newRow]);
    setHasChanges(true);
    dispatch(managementOp.setDataEdited(true));
  };

  const { handleDelete: handleTaskTypeDelete } = useDeleteWithDependencyCheck({
    tableName: 'task_types',
    softDeleteFn: (id) => ApiService.softDeleteRecord('task_types', id),
    onSuccess: (id) => setRows((prev) => prev.filter((r) => r.id !== id)),
    entityLabel: 'task type',
    lang
  });

  const columns: ColumnsType<ITaskType> = useMemo(
    () => [
      {
        title: t(TASK_TYPE_COLUMNS.ACTION),
        key: TASK_TYPE_KEYS.ACTION,
        width: 100,
        render: (_: string, record: ITaskType) => (
          <Styled.StyledButtonSection token={token}>
            <Styled.StyledButton
              type='primary'
              size='small'
              shape='circle'
              icon={<DeleteOutlined />}
              disabled={!record.id}
              onClick={() => record.id && handleTaskTypeDelete(record.id)}
            />
          </Styled.StyledButtonSection>
        )
      },
      {
        title: t(TASK_TYPE_COLUMNS.SHORT_NAME),
        dataIndex: TASK_TYPE_KEYS.SHORT_NAME,
        key: TASK_TYPE_KEYS.SHORT_NAME,
        width: 425,
        render: (val: string, data: ITaskType) => (
          <Styled.ColumnTextBox
            width={500}
            value={data.short_name}
            placeholder='Enter task type name'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange(data.key!, 'short_name', e.target.value)
            }
          />
        )
      },
      {
        title: t(TASK_TYPE_COLUMNS.DESCRIPTION),
        dataIndex: TASK_TYPE_KEYS.DESCRIPTION,
        key: TASK_TYPE_KEYS.DESCRIPTION,
        width: 425,
        render: (val: string, data: ITaskType) => (
          <Styled.ColumnTextBox
            width={425}
            value={data.description}
            placeholder='Enter task type description'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange(data.key!, 'description', e.target.value)
            }
          />
        )
      }
    ],
    [t, token]
  );

  // Remove the individual column definitions since they're now in the useMemo
  // const actionColumn = ...
  // const shortNameColumn = ...
  // const descriptionColumn = ...;

  return (
    <DataManagementLayout>
      <Styled.ContentWrapper>
        {drawTable ? (
          <>
            <CustomTable
              drawTable={drawTable}
              columns={columns}
              dataSource={rows}
              itemsCount={rows.length}
              pagination={false}
              scroll={{ x: 1000 }}
            />
            <Button
              type='primary'
              onClick={handleAddRow}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </>
        ) : (
          <CustomTable
            drawTable={drawTable}
            itemsCount={0}
            columns={columns}
            dataSource={[]}
            pagination={false}
          />
        )}
      </Styled.ContentWrapper>
    </DataManagementLayout>
  );
};

export default TaskTypeManagement;
